import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ComponentEvent, StateAwareComponent} from "../../../model/rest/base";
import {combineLatest, forkJoin, of, Subject} from "rxjs";
import {catchError, filter, map, switchMap, tap} from "rxjs/operators";
import * as LessonsV3 from "../../../model/v3/lessons";
import {ManagerColV3RestService} from "../../../services/rest/v3/manager-col-v3-rest.service";
import {Dates} from "../../../services/utils/calendar-utils";

export class StudentProgressComponentEvent extends ComponentEvent {}

@Component({
    selector: 'app-student-context-based-progress',
    templateUrl: './student-context-based-progress.component.html',
    styleUrls: ['./student-context-based-progress.component.css'],
    standalone: false
})
export class StudentContextBasedProgressComponent extends StateAwareComponent<StudentProgressComponentEvent> implements OnInit, OnDestroy {

  @Input()
  set studentId(value:number) {
    this.studentIdSubject.next(value);
  }
  @Input()
  set schoolId(value: number) {
    this.schoolIdSubject.next(value);
  }
  @Input()
  public set langCode(lang: string) {
    this.langCodeSubject.next(lang);
  }

  private courseContexts: LessonsV3.ColCourseContext[];
  private productCourses: LessonsV3.Course[];
  private lessonDetailsById: Map<number, LessonsV3.LFPreview> = new Map();
  private currentIndex: number = null;
  private loaded = false;
  private studentIdSubject = new Subject<number>();
  private schoolIdSubject = new Subject<number>();
  private langCodeSubject = new Subject<string>();

  constructor(
    private managerRest: ManagerColV3RestService) {
    super();

    combineLatest([
      this.studentIdSubject,
      this.schoolIdSubject,
      this.langCodeSubject
    ]).pipe(
      filter<[number, number, string]>(( [ studentId, schoolId, langCode]) => studentId != null && schoolId != null && langCode != null),
      switchMap(([studentId, schoolId, langCode]) => forkJoin({
        courseContexts: this.managerRest.getCourseContexts(schoolId, studentId, langCode).pipe(
          catchError(() => of(null))
        ),
        productCourses: this.managerRest.listProductCourses(schoolId, langCode).pipe(
          catchError(() => of(null))
        )
      }).pipe(
        map(({courseContexts, productCourses}) => ({
          courseContexts,
          productCourses
        })),
        switchMap(({courseContexts, productCourses}) =>
          this.loadLessonDetails(schoolId, courseContexts, studentId).pipe(
            map((lessonDetailsById) => ({courseContexts, productCourses, lessonDetailsById}))
          )
        )
      )),
      tap(({courseContexts, productCourses, lessonDetailsById}) =>
        this.storeData(courseContexts, productCourses, lessonDetailsById)
      )
    ).subscribe();
  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
  }

  private storeData(
    courseContexts: LessonsV3.ColCourseContext[] | null,
    productCourses: LessonsV3.Course[] | null,
    lessonDetailsById: Map<number, LessonsV3.LFPreview> | null
  ) {
    this.loaded = true;
    this.currentIndex = null;
    this.courseContexts = courseContexts ?? [];
    this.productCourses = productCourses ?? [];
    this.lessonDetailsById = lessonDetailsById ?? new Map();
    if (this.courseContexts.length > 0) {
      this.currentIndex = this.courseContexts.findIndex((courseContext) => courseContext?.current);
      if (this.currentIndex < 0) {
        this.currentIndex = this.courseContexts.length - 1;
      }
    }
  }

  hasAnyProgress() {
    return this.currentIndex != null;
  }

  getCurrentCourseName() {
    const courseCode = this.getContext()?.course?.code;
    if (!courseCode) return '';
    return this.productCourses?.find((course) => course.code === courseCode)?.name ?? '';
  }

  hasPrev() {
    return this.currentIndex != null && this.currentIndex > 0;
  }

  hasNext() {
    return this.currentIndex != null && this.currentIndex < this.courseContexts.length - 1;
  }

  moveCourse(number: number) {
    const prevValue = this.currentIndex;
    this.currentIndex += number;
    if (this.currentIndex < 0 || this.currentIndex >= this.courseContexts.length) {
      this.currentIndex = prevValue;
    }
  }

  hasNoProgress() {
    return this.loaded && this.currentIndex == null;
  }

  getProgress() {
    const context = this.getContext();
    if (!context) return 0;
    const result = context.lessonsProgress;
    return result != null ? result * 100 : 0;
  }

  getRevisionProgress() {
    const context = this.getContext();
    if (!context) return 0;
    return context.revisionProgress ? context.revisionProgress * 100 : 0;
  }

  hasAnyLesson() {
    const context = this.getContext();
    return !!context && context.lessons > 0;
  }

  private static getLessonStartDate(lesson: LessonsV3.LFPreview | null) {
    if (!lesson || !lesson.lessonMetric || !lesson.lessonMetric.started) return null;
    const started = lesson.lessonMetric.started;
    if (started instanceof Date) return started;
    return Dates.parse(String(started));
  }

  getFirstLessonDate() {
    const context = this.getContext();
    if (!context) return null;
    return StudentContextBasedProgressComponent.getLessonStartDate(this.getLessonPreview(context.firstLesson));
  }

  private getContext() {
    if (!this.courseContexts || this.currentIndex == null || this.currentIndex < 0 || this.currentIndex >= this.courseContexts.length) {
      return null;
    }
    return this.courseContexts[this.currentIndex];
  }

  getNumberOverallLessons() {
    const context = this.getContext();
    return context ? context.overall : 0;
  }

  getLastLessonDate() {
    const context = this.getContext();
    if (!context) return null;
    const allDates = [
      this.getLastExamDate(),
      StudentContextBasedProgressComponent.getLessonStartDate(this.getLessonPreview(context.lastQuickStageRevision)),
      StudentContextBasedProgressComponent.getLessonStartDate(this.getLessonPreview(context.lastRegularLesson)),
      StudentContextBasedProgressComponent.getLessonStartDate(this.getLessonPreview(context.lastRevision))
    ].filter( el => el != null)
      .sort((l,r) => l.getTime() - r.getTime());
    if (allDates.length === 0) return null;
    return allDates[allDates.length - 1];
  }

  getLastExamDate() {
    const context = this.getContext();
    if (!context) return null;
    return StudentContextBasedProgressComponent.getLessonStartDate(this.getLessonPreview(context.lastExam));
  }

  hasQs() {
    const context = this.getContext();
    if (!context) return false;
    return context.quickStageRevisions > 0;
  }

  private getLessonPreview(lesson: LessonsV3.LessonRef | null): LessonsV3.LFPreview | null {
    if (!lesson) {
      return null;
    }
    return this.lessonDetailsById.get(lesson.id) ?? null;
  }

  private loadLessonDetails(schoolId: number, courseContexts: LessonsV3.ColCourseContext[] | null, studentId: number) {
    const lessonIds = new Set<number>();
    (courseContexts ?? []).forEach((context) => {
      [
        context?.firstLesson,
        context?.lastExam,
        context?.lastRevision,
        context?.lastQuickStageRevision,
        context?.lastRegularLesson
      ].forEach((lesson) => {
        if (lesson?.id > 0) {
          lessonIds.add(lesson.id);
        }
      });
    });
    if (lessonIds.size === 0) {
      return of(new Map<number, LessonsV3.LFPreview>());
    }
    return forkJoin(
      Array.from(lessonIds).map((lessonId) =>
        this.managerRest.getLessonDetails(schoolId, lessonId, 'teacher-full', studentId).pipe(
          catchError(() => of(null))
        )
      )
    ).pipe(
      map((lessons) => {
        const result = new Map<number, LessonsV3.LFPreview>();
        lessons.forEach((lesson) => {
          if (lesson && 'lessonMetric' in lesson) {
            result.set((lesson as LessonsV3.LFPreview).id, lesson as LessonsV3.LFPreview);
          }
        });
        return result;
      })
    );
  }

  getExamProgressWidth() {
    const weight = 5;
    const context = this.getContext();
    const progress = context?.exams ? 100 : 0;
    return this.widthStyle(progress, weight);
  }

  getRevisionProgressWidth() {
    const weight = this.hasQs() ? 10 : 15;
    const progress = this.getRevisionProgress();
    return this.widthStyle(progress, weight);
  }

  getLessonProgressWidth() {
    const weight = this.hasQs() ? 70 : 80;
    const progress = this.getProgress();
    return this.widthStyle(progress, weight);
  }

  private widthStyle(width: number, weight: number) {
    return {width: `${width * weight / 100}%`};
  }

  getExamProgressDescription() {
    return this.getContext()?.exams ? '✓' : '---';
  }
}
